import { useMemo, useState } from 'react';
import { useTenant } from '@/hooks/use-tenant';
import {
  useGetBoardsQuery,
  useGetEligibleUsersQuery,
  useAddUsersToProjectMutation,
  useDeleteUserFromProjectMutation,
  useGetCurrentProjectMembersQuery,
  useGetClientUsersListQuery,
  useAddUserToExternalProjectMutation,
  useDeleteUserFromExternalProjectMutation,
} from '@/state/project/project-api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2,
  UserPlus,
  Users as UsersIcon,
  CheckSquare,
  Square,
  Trash2,
  Search,
  Eye,
} from 'lucide-react';
import type { ProjectResponse, CurrentProjectMember } from '@/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getInitials, cn } from '@/lib/utils';
import { getStatusLabel } from '@/lib';
import { UserSkillViewModal } from './show-project-details/user-skill-view-modal';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { useGetCurrentUserQuery } from '@/state';

interface ProjectUsersProps {
  projectId?: number;
  resourceId?: number;
  projectDetails?: ProjectResponse;
  isReadOnly?: boolean;
}

export const ProjectUsers = ({
  projectId,
  resourceId,
  projectDetails,
  isReadOnly = false,
}: ProjectUsersProps) => {
  const { tenant } = useTenant();
  const { isStudent } = useClientPermissions();
  const {  data: currentUser } = useGetCurrentUserQuery({tenant});
  const [selectedBoard, setSelectedBoard] = useState<string>('');
  const [showAll, setShowAll] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedViewMember, setSelectedViewMember] =
    useState<CurrentProjectMember | null>(null);
  // External project user addition state
  const [selectedExternalUsers, setSelectedExternalUsers] = useState<
    Set<string>
  >(new Set());
  const [isAddingExternal, setIsAddingExternal] = useState(false);

  const effectiveProjectId = projectId || projectDetails?.project_id;

  const { data: boards, isLoading: loadingBoards } = useGetBoardsQuery(
    { tenant: tenant!, projectId: effectiveProjectId },
    { skip: !tenant || !effectiveProjectId }
  );

  const isExternal = projectDetails?.project_type === 2;

  const { data: internalMembers, isLoading: loadingInternalMembers } =
    useGetCurrentProjectMembersQuery(
      { tenant: tenant!, projectId: effectiveProjectId! },
      { skip: !tenant || !effectiveProjectId || isExternal }
    );

  const isProjectMember =  internalMembers?.some(m => m.id === currentUser?.id) || projectDetails?.public_project_candidates?.some(pc => pc.candidate.id === currentUser?.id);

  const members: CurrentProjectMember[] = useMemo(() => {
    if (isExternal) {
      return (projectDetails?.public_project_candidates || []).map((pc) => ({
        id: pc.candidate.id,
        candidateId: pc.id,
        email: pc.candidate.email,
        first_name: pc.candidate.first_name,
        last_name: pc.candidate.last_name,
        role_display: pc.role === 1 ? 'Mentor' : 'Student',
        status: pc.status,
        photo_url:
          pc.candidate.photo_url || pc.candidate.profile?.photo_url || null,
        schema_id: 0,
        schema_name: '',
        school_name: '',
      }));
    }
    return internalMembers || [];
  }, [isExternal, projectDetails?.public_project_candidates, internalMembers]);

  const loadingMembers = isExternal ? false : loadingInternalMembers;

  const boardId = selectedBoard ? parseInt(selectedBoard) : undefined;

  const { data: eligibleUsers, isLoading: loadingUsers } =
    useGetEligibleUsersQuery(
      {
        tenant: tenant!,
        projectId: effectiveProjectId!,
        boardId: showAll ? undefined : boardId,
      },
      { skip: !tenant || (!showAll && !selectedBoard) || !effectiveProjectId }
    );

  const [addUsers, { isLoading: isAdding }] = useAddUsersToProjectMutation();
  const [deleteUser, { isLoading: isDeletingInternal }] =
    useDeleteUserFromProjectMutation();
  const [addUserToExternal] = useAddUserToExternalProjectMutation();
  const [deleteExternalUser, { isLoading: isDeletingExternal }] =
    useDeleteUserFromExternalProjectMutation();

  const isDeleting = isDeletingInternal || isDeletingExternal;

  // Fetch client users list for external project add-user section
  const { data: clientUsersList, isLoading: loadingClientUsers } =
    useGetClientUsersListQuery(
      { tenant: tenant! },
      { skip: !tenant || !isExternal }
    );

  // Filter out users already in the project
  const availableClientUsers = useMemo(() => {
    if (!clientUsersList) return [];
    return clientUsersList.filter((u) => !members.some((m) => m.id === u.id));
  }, [clientUsersList, members]);

  const handleSelectUser = (userId: string) => {
    // Prevent selecting already added members
    const isAlreadyMember = members?.some((m) => m.id === userId);
    if (isAlreadyMember) return;

    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    if (eligibleUsers) {
      if (selectedUsers.size === eligibleUsers.length) {
        setSelectedUsers(new Set());
      } else {
        setSelectedUsers(new Set(eligibleUsers.map((u) => u.id)));
      }
    }
  };

  const handleAddUsers = async () => {
    if (!tenant || selectedUsers.size === 0 || !effectiveProjectId) return;

    const userDetails = Array.from(selectedUsers).map((userId) => {
      const user = eligibleUsers?.find((u) => u.id === userId);

      const isAdminOrTeacher = user?.role?.some((role) =>
        ['school admin', 'teacher'].includes(role.toLowerCase())
      );

      return {
        id: userId,
        candidate: userId,
        project: effectiveProjectId,
        role: isAdminOrTeacher ? 1 : 2,
      };
    });

    try {
        await addUsers({
            tenant,
            data: { user_details: userDetails },
        }).unwrap();
        toast.success('Users added successfully');
        setSelectedUsers(new Set());
    } catch (error: unknown) {
        const err = error as { data?: { message?: string } };
        toast.error(err?.data?.message || 'Failed to add users');
    }
  };

  const handleDeleteUser = async (member: CurrentProjectMember) => {
    if (!tenant || !effectiveProjectId) return;

    try {
      if (isExternal && member.candidateId) {
        await deleteExternalUser({
          candidateId: member.candidateId,
          projectId: resourceId || effectiveProjectId!,
        }).unwrap();
      } else {
        await deleteUser({
          tenant,
          memberId: member.id,
          projectListId: effectiveProjectId!,
        }).unwrap();
      }
      toast.success('User removed from project');
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } };
      toast.error(err?.data?.message || 'Failed to remove user');
    }
  };

  const handleToggleShowAll = () => {
    setShowAll(true);
    setSelectedBoard('');
    setSelectedUsers(new Set());
  };

  const handleBoardChange = (val: string) => {
    setSelectedBoard(val);
    setShowAll(false);
    setSelectedUsers(new Set());
  };

  // ---------- External-only handlers ----------
  const handleSelectExternalUser = (userId: string) => {
    const next = new Set(selectedExternalUsers);
    if (next.has(userId)) {
      next.delete(userId);
    } else {
      next.add(userId);
    }
    setSelectedExternalUsers(next);
  };

  const handleSelectAllExternal = () => {
    if (selectedExternalUsers.size === availableClientUsers.length) {
      setSelectedExternalUsers(new Set());
    } else {
      setSelectedExternalUsers(new Set(availableClientUsers.map((u) => u.id)));
    }
  };

  const handleAddExternalUsers = async () => {
    if (!effectiveProjectId || selectedExternalUsers.size === 0) return;
    setIsAddingExternal(true);
    let successCount = 0;
    let failCount = 0;
    for (const userId of Array.from(selectedExternalUsers)) {
      try {
        await addUserToExternal({
          id: userId,
          candidate: userId,
          project: String(resourceId || effectiveProjectId),
          status: 2,
        }).unwrap();
        successCount++;
      } catch {
        failCount++;
      }
    }
    setIsAddingExternal(false);
    setSelectedExternalUsers(new Set());
    if (successCount > 0)
      toast.success(
        `${successCount} user${successCount !== 1 ? 's' : ''} added to project`
      );
    if (failCount > 0)
      toast.error(
        `${failCount} user${failCount !== 1 ? 's' : ''} failed to add`
      );
  };

  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardHeader className="px-0 pt-0">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-primary" />
            Project Members
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-0 space-y-8">
        {/* Existing Members Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium flex items-center gap-2">
              Current Members
              <span className="text-xs font-normal bg-emerald-500/10 text-emerald-600 px-2 py-0.5 rounded-full">
                {members?.length ?? 0} member{members?.length !== 1 ? 's' : ''}
              </span>
            </h3>
          </div>

          {loadingMembers ? (
            <div className="flex flex-col items-center justify-center py-10 bg-muted/10 rounded-2xl border-2 border-dashed border-muted/50">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-muted-foreground text-sm mt-4">
                Loading members...
              </p>
            </div>
          ) : (members?.length ?? 0) > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {members?.map((member) => (
                <div
                  key={member.id}
                  className="group flex items-center space-x-3 p-4 rounded-2xl border-2 border-border bg-background hover:border-primary/20 transition-all"
                >
                  <Avatar className="size-10 rounded-full border-2 border-background shadow-sm">
                    {member.photo_url && (
                      <AvatarImage
                        src={member.photo_url}
                        alt={`${member.first_name} ${member.last_name}`}
                      />
                    )}
                    <AvatarFallback className="bg-primary/10 text-primary font-bold text-xs">
                      {getInitials(
                        member.first_name || '',
                        member.last_name || ''
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold leading-none truncate mb-1">
                      {member.first_name || 'No Name'} {member.last_name || ''}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {member.email}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider font-semibold bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                        {member.role_display}
                      </span>
                      <span
                        className={cn(
                          'text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded',
                          member.status === 2
                            ? 'bg-emerald-500/10 text-emerald-600'
                            : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {getStatusLabel(member.status)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 transition-all opacity-0 group-hover:opacity-100">
                    {(!isStudent || currentUser?.id === member.id) && 
                      member.role_display?.toLowerCase() === 'student' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-primary hover:text-primary hover:bg-primary/10 h-8 w-8"
                          onClick={() => {
                            setSelectedViewMember(member);
                            setShowViewModal(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}

                    {!isReadOnly && isProjectMember && !isStudent &&
                      member.role_display?.toLowerCase() !== 'mentor' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                              disabled={isDeleting}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-2xl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Remove member?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to remove{' '}
                                {member.first_name} {member.last_name} from this
                                project? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteUser(member)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-muted/20 rounded-2xl border-2 border-dashed border-muted">
              <p className="text-muted-foreground text-sm">
                No members added yet.
              </p>
            </div>
          )}
        </div>

        <UserSkillViewModal
          open={showViewModal}
          onOpenChange={setShowViewModal}
          userId={selectedViewMember?.id || ''}
          userName={`${selectedViewMember?.first_name || ''} ${selectedViewMember?.last_name || ''}`}
          projectId={effectiveProjectId!}
          evaluationSchema={tenant || 'undefined'}
        />

        {/* ---- External Project: Add Users ---- */}
        {isExternal && !isReadOnly && (
          <>
            <div className="h-px bg-border" />
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  Add Members to Project
                  {availableClientUsers.length > 0 && (
                    <span className="text-xs font-normal bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      {availableClientUsers.length} available
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  {availableClientUsers.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleSelectAllExternal}
                      className="text-primary hover:text-primary/80 hover:bg-primary/5 rounded-lg h-8 text-xs font-semibold"
                    >
                      {selectedExternalUsers.size ===
                      availableClientUsers.length ? (
                        <>
                          <Square className="w-3.5 h-3.5 mr-1.5" />
                          DESELECT ALL
                        </>
                      ) : (
                        <>
                          <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                          SELECT ALL
                        </>
                      )}
                    </Button>
                  )}
                  {selectedExternalUsers.size > 0 && (
                    <Button
                      onClick={handleAddExternalUsers}
                      disabled={isAddingExternal}
                      size="sm"
                    >
                      {isAddingExternal ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <UserPlus className="w-4 h-4 mr-2" />
                      )}
                      Add {selectedExternalUsers.size} User
                      {selectedExternalUsers.size !== 1 ? 's' : ''}
                    </Button>
                  )}
                </div>
              </div>

              {loadingClientUsers ? (
                <div className="flex flex-col items-center justify-center py-20 bg-muted/10 rounded-2xl border-2 border-dashed border-muted/50">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-muted-foreground text-sm mt-4">
                    Loading users...
                  </p>
                </div>
              ) : availableClientUsers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {availableClientUsers.map((user) => {
                    const isSelected = selectedExternalUsers.has(user.id);
                    const firstName =
                      user.profile?.first_name || user.email.split('@')[0];
                    const lastName = user.profile?.last_name || '';
                    const photoUrl = user.profile?.photo_url || null;
                    const roleLabel =
                      user.user_in_client_roles?.[0] ||
                      user.roles?.[0] ||
                      'User';

                    return (
                      <div
                        key={user.id}
                        className={cn(
                          'group relative flex items-center space-x-3 p-3 border-2 rounded-xl transition-all cursor-pointer',
                          isSelected
                            ? 'border-primary bg-primary/[0.03] ring-2 ring-primary/10'
                            : 'border-border hover:border-primary/30 hover:bg-muted/10'
                        )}
                        onClick={() => handleSelectExternalUser(user.id)}
                      >
                        <div className="flex items-center h-full">
                          <Checkbox
                            id={`ext-${user.id}`}
                            checked={isSelected}
                            onCheckedChange={() =>
                              handleSelectExternalUser(user.id)
                            }
                            className="rounded h-4 w-4 border-2 transition-colors"
                          />
                        </div>
                        <Avatar className="size-9 rounded-full border-2 border-background shadow-sm shrink-0">
                          {photoUrl && (
                            <AvatarImage
                              src={photoUrl}
                              alt={`${firstName} ${lastName}`}
                            />
                          )}
                          <AvatarFallback className="bg-primary/10 text-primary font-bold text-xs">
                            {getInitials(firstName, lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold leading-tight truncate">
                            {firstName} {lastName}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.email}
                          </p>
                          <span className="text-[10px] uppercase tracking-wider font-semibold bg-muted px-1.5 py-0.5 rounded text-muted-foreground mt-1 inline-block">
                            {roleLabel}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16 bg-muted/10 rounded-2xl border-2 border-dashed border-muted/50">
                  <UsersIcon className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm font-medium">
                    {clientUsersList && clientUsersList.length > 0
                      ? 'All available users are already members of this project.'
                      : 'No users found in this organization.'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {!isExternal && !isReadOnly && isProjectMember && (
          <>
            <div className="h-px bg-border" />

            {/* Add Users Section */}
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <h3 className="text-lg font-medium flex items-center gap-2">
                  Add New Members
                </h3>
                {selectedUsers.size > 0 && (
                  <Button onClick={handleAddUsers} disabled={isAdding}>
                    {isAdding ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4 mr-2" />
                    )}
                    Add {selectedUsers.size} Selected Users
                  </Button>
                )}
              </div>

              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-2 flex-1 min-w-[280px]">
                  <label className="text-sm font-medium text-muted-foreground ml-1">
                    Select Board
                  </label>
                  <Select
                    value={selectedBoard}
                    onValueChange={handleBoardChange}
                  >
                    <SelectTrigger className="w-full  border-2 focus:ring-primary/20 transition-all">
                      <SelectValue
                        placeholder={
                          loadingBoards
                            ? 'Loading boards...'
                            : 'Fetch users from a board'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent className="">
                      {boards?.map((board) => (
                        <SelectItem key={board.id} value={board.id.toString()}>
                          {board.board_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="pb-[2px]">
                  <span className="text-xs font-bold text-muted-foreground px-2">
                    OR
                  </span>
                </div>

                <Button
                  variant="outline"
                  onClick={handleToggleShowAll}
                  className={cn(
                    ' border-2 h-[42px] px-6 transition-all',
                    showAll
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'hover:border-primary/20'
                  )}
                >
                  <Search className="w-4 h-4 mr-2" />
                  View All Eligible Users
                </Button>
              </div>

              {(selectedBoard || showAll) && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-muted-foreground px-1">
                      {showAll ? 'ALL ELIGIBLE USERS' : 'BOARD USERS'}
                      {eligibleUsers && (
                        <span className="ml-2 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                          {eligibleUsers.length} AVAILABLE
                        </span>
                      )}
                    </h3>
                    {eligibleUsers && eligibleUsers.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAll}
                        className="text-primary hover:text-primary/80 hover:bg-primary/5 rounded-lg h-8 text-xs font-semibold"
                      >
                        {selectedUsers.size === eligibleUsers.length ? (
                          <>
                            <Square className="w-3.5 h-3.5 mr-1.5" />
                            DESELECT ALL
                          </>
                        ) : (
                          <>
                            <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                            SELECT ALL
                          </>
                        )}
                      </Button>
                    )}
                  </div>

                  {loadingUsers ? (
                    <div className="flex flex-col items-center justify-center py-20 bg-muted/10 rounded-2xl border-2 border-dashed border-muted/50">
                      <Loader2 className="w-8 h-8 animate-spin text-primary" />
                      <p className="text-muted-foreground text-sm mt-4">
                        Searching for users...
                      </p>
                    </div>
                  ) : eligibleUsers && eligibleUsers.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {eligibleUsers.map((user) => {
                        const isAlreadyMember = members?.some(
                          (m) => m.id === user.id
                        );
                        const isSelected = selectedUsers.has(user.id);

                        return (
                          <div
                            key={user.id}
                            className={cn(
                              'group relative flex items-center space-x-3 p-3 border-2 transition-all',
                              isAlreadyMember
                                ? 'opacity-60 cursor-not-allowed bg-muted/5 border-muted'
                                : isSelected
                                  ? 'border-primary bg-primary/[0.03] ring-2 ring-primary/5 cursor-pointer'
                                  : 'border-border hover:border-primary/20 hover:bg-muted/10 cursor-pointer'
                            )}
                            onClick={() =>
                              !isAlreadyMember && handleSelectUser(user.id)
                            }
                          >
                            <div className="flex items-center h-full">
                              <Checkbox
                                id={user.id}
                                checked={isAlreadyMember || isSelected}
                                disabled={isAlreadyMember}
                                onCheckedChange={() =>
                                  !isAlreadyMember && handleSelectUser(user.id)
                                }
                                className={cn(
                                  'rounded h-4 w-4 border-2 transition-colors',
                                  isAlreadyMember &&
                                    'bg-muted-foreground/20 border-muted-foreground/30'
                                )}
                              />
                            </div>
                            <Avatar className="size-8 rounded-full border-2 border-background shadow-sm shrink-0">
                              {user.photo_url && (
                                <AvatarImage
                                  src={user.photo_url}
                                  alt={`${user.first_name} ${user.last_name}`}
                                />
                              )}
                              <AvatarFallback className="bg-primary/10 text-primary font-bold text-[10px]">
                                {getInitials(
                                  user.first_name || '',
                                  user.last_name || ''
                                )}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0 pr-12">
                              <div className="flex items-center gap-1.5 mb-1">
                                <p className="text-xs font-bold leading-none truncate">
                                  {user.first_name || 'No Name'}{' '}
                                  {user.last_name || ''}
                                </p>
                                {isAlreadyMember && (
                                  <Badge
                                    variant="secondary"
                                    className="px-1.5 py-0 h-4 text-[8px] font-bold bg-muted-foreground/10 text-muted-foreground uppercase border-0"
                                  >
                                    Member
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground truncate">
                                {user.email}
                              </p>
                            </div>

                            {isAlreadyMember && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <CheckSquare className="w-4 h-4 text-muted-foreground opacity-50" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-16 bg-muted/10 rounded-2xl border-2 border-dashed border-muted/50">
                      <UsersIcon className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm font-medium">
                        No users found for this selection.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
